import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Table, Button, Badge, Modal, Form, Alert, Spinner, ProgressBar, Row, Col, Tabs, Tab, ButtonGroup } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFilePdf, faPen, faCheck, faBan, faRotate, faPlus, faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import {
  getCopertineLotto, rilanciaRicercaLotto, aggiungiAlLotto, generaPdfLotto, modificaVoceCopertina,
  cercaVoceCopertina, scegliVoceCopertina, anteprimaPdfVoce, caricaLocandinaVoce, eliminaVoceCopertina,
  getPaletteCopertine, aggiornaLottoCopertine, completaGeneriLotto,
} from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Segnalazioni: etichetta e gravità (danger = da sistemare prima di stampare)
const AVVISI = {
  non_trovato: ['Non trovato', 'danger'],
  errore_ricerca: ['Errore di ricerca', 'danger'],
  locandina_mancante: ['Locandina mancante', 'danger'],
  trama_mancante: ['Trama mancante', 'danger'],
  trama_troncata: ['Trama accorciata per lo spazio', 'danger'],
  attori_tagliati: ['Attori in più tolti per lo spazio', 'danger'],
  caratteri_non_supportati: ['Caratteri non stampabili', 'danger'],
  regista_mancante: ['Regista mancante', 'warning'],
  attori_mancanti: ['Attori mancanti', 'warning'],
  trama_non_italiana: ['Trama in inglese', 'warning'],
  locandina_bassa_risoluzione: ['Locandina a bassa risoluzione', 'warning'],
  titolo_originale_non_latino: ['Titolo originale non latino: usato l\'inglese', 'warning'],
  genere_mancante: ['Genere mancante', 'secondary'],
  trama_ridotta: ['Trama rimpicciolita', 'secondary'],
  titolo_ridotto: ['Titolo rimpicciolito', 'secondary'],
  titolo_dorso_ridotto: ['Titolo del dorso rimpicciolito', 'secondary'],
  attori_ridotti: ['Elenco attori rimpicciolito', 'secondary'],
};

const urlLocandina = (v, misura = 'w342') => {
  if (v.poster_locale) return `${API_BASE}/api/uploads/copertine/${v.poster_locale}`;
  return v.poster_url ? v.poster_url.replace('/original/', `/${misura}/`) : null;
};

const BadgeAvviso = ({ codice }) => {
  const [etichetta, variante] = AVVISI[codice] || [codice, 'secondary'];
  return <Badge bg={variante} text={variante === 'warning' ? 'dark' : undefined} className="me-1 mb-1 fw-normal">{etichetta}</Badge>;
};

const BadgeStato = ({ voce }) => {
  if (voce.escluso) return <Badge bg="secondary">Escluso</Badge>;
  if (voce.confermato) return <Badge bg="success">Confermato</Badge>;
  if (voce.stato === 'trovato') return <Badge bg="success">Trovato</Badge>;
  if (voce.stato === 'dubbio') return <Badge bg="warning" text="dark">Da controllare</Badge>;
  if (voce.stato === 'non_trovato') return <Badge bg="danger">Non trovato</Badge>;
  return <Badge bg="info"><Spinner size="sm" animation="border" /> In ricerca</Badge>;
};

function CopertineDvdLotto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lotto, setLotto] = useState(null);
  const [error, setError] = useState(null);
  const [palette, setPalette] = useState([]);
  const [filtro, setFiltro] = useState('tutti');
  const [cerca, setCerca] = useState('');

  // modale di modifica
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [ricerca, setRicerca] = useState({ titolo: '', regista: '', anno: '', tipo: 'film' });
  const [tmdbManuale, setTmdbManuale] = useState('');
  const [occupato, setOccupato] = useState(false);

  // aggiunta film
  const [showAggiungi, setShowAggiungi] = useState(false);
  const [testoAggiunta, setTestoAggiunta] = useState('');

  const carica = useCallback(async () => {
    try {
      const res = await getCopertineLotto(id);
      setLotto(res.data);
    } catch (err) {
      setError('Errore durante il caricamento del lotto.');
      console.error(err);
    }
  }, [id]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    getPaletteCopertine().then(res => setPalette(res.data)).catch(err => console.error('Errore palette:', err));
  }, []);

  // finché la ricerca o il PDF sono in corso, si ricarica ogni 2 secondi
  const attivo = lotto && (lotto.stato_ricerca === 'in_corso' || lotto.pdf_stato === 'generating');
  useEffect(() => {
    if (!attivo) return undefined;
    const timer = setInterval(carica, 2000);
    return () => clearInterval(timer);
  }, [attivo, carica]);

  const voci = lotto ? lotto.voci : [];
  const voceInModifica = voci.find(v => v.id === editId) || null;

  const aggiornaVoce = (voceAgg) => setLotto(prev => ({ ...prev, voci: prev.voci.map(v => (v.id === voceAgg.id ? voceAgg : v)) }));

  const riepilogo = useMemo(() => ({
    totale: voci.length,
    ok: voci.filter(v => !v.escluso && (v.stato === 'trovato' || v.confermato)).length,
    dubbi: voci.filter(v => !v.escluso && v.stato === 'dubbio' && !v.confermato).length,
    nonTrovati: voci.filter(v => !v.escluso && v.stato === 'non_trovato' && !v.confermato).length,
    esclusi: voci.filter(v => v.escluso).length,
    daStampare: voci.filter(v => !v.escluso && v.titolo_originale).length,
  }), [voci]);

  const visibili = voci.filter(v => {
    if (filtro === 'controllare' && !(!v.escluso && !v.confermato && (v.stato === 'dubbio' || v.stato === 'non_trovato'))) return false;
    if (filtro === 'avvisi' && !(v.avvisi || []).some(a => AVVISI[a] && AVVISI[a][1] !== 'secondary')) return false;
    if (filtro === 'esclusi' && !v.escluso) return false;
    if (cerca.trim()) {
      const t = cerca.trim().toLowerCase();
      return [v.riga_originale, v.titolo_originale, v.titolo_italiano, v.registi].some(x => (x || '').toLowerCase().includes(t));
    }
    return true;
  });

  // --- azioni sul lotto ---
  const apriPdf = (percorso) => window.open(`${API_BASE}${percorso}?t=${Date.now()}`, '_blank');

  const rilancia = async () => {
    try {
      const res = await rilanciaRicercaLotto(id);
      setLotto(res.data);
    } catch (err) { setError('Impossibile riavviare la ricerca.'); }
  };

  const aggiungi = async () => {
    try {
      setOccupato(true);
      const res = await aggiungiAlLotto(id, testoAggiunta);
      setLotto(res.data);
      setShowAggiungi(false);
      setTestoAggiunta('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile aggiungere i film.');
    } finally { setOccupato(false); }
  };

  const cambiaDorsoColorato = async (valore) => {
    try {
      const res = await aggiornaLottoCopertine(id, { dorso_colorato: valore });
      setLotto(res.data);
    } catch (err) { setError('Impossibile cambiare l\'impostazione del dorso.'); }
  };

  const completaGeneri = async () => {
    try {
      setOccupato(true);
      const res = await completaGeneriLotto(id);
      setLotto(res.data);
    } catch (err) {
      setError('Impossibile recuperare i generi da TMDB.');
    } finally { setOccupato(false); }
  };

  const generaPdf = async () => {
    try {
      const res = await generaPdfLotto(id);
      setLotto(res.data);
    } catch (err) { setError('Impossibile avviare la generazione del PDF.'); }
  };

  // --- azioni sulla singola voce ---
  const alterna = async (voce, campo) => {
    try {
      const res = await modificaVoceCopertina(voce.id, { [campo]: !voce[campo] });
      aggiornaVoce(res.data);
    } catch (err) { setError('Salvataggio non riuscito.'); }
  };

  const elimina = async (voce) => {
    if (!window.confirm(`Togliere "${voce.riga_originale}" dal lotto?`)) return;
    try {
      await eliminaVoceCopertina(voce.id);
      setLotto(prev => ({ ...prev, voci: prev.voci.filter(v => v.id !== voce.id) }));
    } catch (err) { setError('Eliminazione non riuscita.'); }
  };

  const apriModifica = (voce) => {
    setEditId(voce.id);
    setRicerca({ titolo: voce.titolo_cercato || '', regista: voce.regista_cercato || '', anno: voce.anno_cercato || '', tipo: voce.tipo || 'film' });
    setTmdbManuale('');
  };

  // il modulo si riallinea ai dati quando si apre una voce o se ne sceglie un'altra
  useEffect(() => {
    if (!voceInModifica) return;
    setForm({
      titolo_originale: voceInModifica.titolo_originale || '',
      titolo_italiano: voceInModifica.titolo_italiano || '',
      anno: voceInModifica.anno || '',
      etichetta_regia: voceInModifica.etichetta_regia || 'REGISTA:',
      registi: voceInModifica.registi || '',
      attori: voceInModifica.attori || '',
      generi: voceInModifica.generi || '',
      colore_dorso: voceInModifica.colore_dorso || '',
      trama: voceInModifica.trama || '',
      poster_url: voceInModifica.poster_url || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, voceInModifica && voceInModifica.tmdb_id, voceInModifica && voceInModifica.poster_locale]);

  const cambiaForm = (campo, valore) => setForm(prev => ({ ...prev, [campo]: valore }));

  const salva = async (conferma = false) => {
    const dati = {
      titolo_originale: form.titolo_originale, titolo_italiano: form.titolo_italiano,
      anno: form.anno === '' ? null : Number(form.anno), etichetta_regia: form.etichetta_regia,
      registi: form.registi, attori: form.attori, trama: form.trama,
      generi: form.generi, colore_dorso: form.colore_dorso || '',
    };
    if (form.poster_url !== voceInModifica.poster_url && form.poster_url) dati.poster_url = form.poster_url;
    if (conferma) dati.confermato = true;
    const res = await modificaVoceCopertina(voceInModifica.id, dati);
    aggiornaVoce(res.data);
    return res.data;
  };

  const salvaEChiudi = async (conferma) => {
    try {
      setOccupato(true);
      await salva(conferma);
      setEditId(null);
    } catch (err) {
      setError('Salvataggio non riuscito: ' + (err.response?.data?.detail || err.message));
    } finally { setOccupato(false); }
  };

  const anteprima = async () => {
    const finestra = window.open('', '_blank'); // aperta subito per non farla bloccare dal browser
    try {
      setOccupato(true);
      await salva(false);
      const res = await anteprimaPdfVoce(voceInModifica.id);
      if (finestra) finestra.location = `${API_BASE}${res.data.pdf_path}`; else apriPdf(res.data.pdf_path);
      carica();
    } catch (err) {
      if (finestra) finestra.close();
      setError('Anteprima non riuscita: ' + (err.response?.data?.detail || err.message));
    } finally { setOccupato(false); }
  };

  const cercaFilm = async () => {
    try {
      setOccupato(true);
      const res = await cercaVoceCopertina(voceInModifica.id, {
        titolo: ricerca.titolo, regista: ricerca.regista || null, anno: ricerca.anno ? Number(ricerca.anno) : null, tipo: ricerca.tipo,
      });
      aggiornaVoce(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ricerca non riuscita.');
    } finally { setOccupato(false); }
  };

  const scegli = async (tmdbId, tipo) => {
    try {
      setOccupato(true);
      const res = await scegliVoceCopertina(voceInModifica.id, tmdbId, tipo);
      aggiornaVoce(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossibile caricare il film scelto.');
    } finally { setOccupato(false); }
  };

  const caricaFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setOccupato(true);
      const res = await caricaLocandinaVoce(voceInModifica.id, file);
      aggiornaVoce(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Caricamento non riuscito.');
    } finally { setOccupato(false); e.target.value = ''; }
  };

  if (!lotto) {
    return <Container className="text-center mt-5">{error ? <Alert variant="danger">{error}</Alert> : <Spinner animation="border" />}</Container>;
  }

  const posterModulo = voceInModifica
    ? (form.poster_url && form.poster_url !== voceInModifica.poster_url
      ? form.poster_url.replace('/original/', '/w342/')
      : urlLocandina(voceInModifica))
    : null;

  return (
    <Container fluid className="p-4">
      <Button variant="link" className="p-0 mb-2" onClick={() => navigate('/copertine-dvd')}><FontAwesomeIcon icon={faArrowLeft} /> Tutti i lotti</Button>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h2 className="mb-1">{lotto.nome}</h2>
          <div>
            <Badge bg="secondary" className="me-1">{riepilogo.totale} film</Badge>
            <Badge bg="success" className="me-1">{riepilogo.ok} a posto</Badge>
            {riepilogo.dubbi > 0 && <Badge bg="warning" text="dark" className="me-1">{riepilogo.dubbi} da controllare</Badge>}
            {riepilogo.nonTrovati > 0 && <Badge bg="danger" className="me-1">{riepilogo.nonTrovati} non trovati</Badge>}
            {riepilogo.esclusi > 0 && <Badge bg="secondary" className="me-1">{riepilogo.esclusi} esclusi</Badge>}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-secondary" onClick={() => setShowAggiungi(true)}><FontAwesomeIcon icon={faPlus} /> Aggiungi film</Button>
          <Button variant="outline-secondary" onClick={rilancia} disabled={lotto.stato_ricerca === 'in_corso'} title="Ripete la ricerca per i film non trovati e non confermati">
            <FontAwesomeIcon icon={faRotate} /> Cerca di nuovo i non trovati
          </Button>
          {voci.some(v => v.tmdb_id && !(v.generi || '').trim()) && (
            <Button variant="outline-warning" onClick={completaGeneri} disabled={occupato} title="Recupera da TMDB il genere dei film già abbinati">
              Completa i generi
            </Button>
          )}
          <Button variant="success" onClick={generaPdf} disabled={lotto.pdf_stato === 'generating' || riepilogo.daStampare === 0}>
            {lotto.pdf_stato === 'generating' ? <><Spinner size="sm" animation="border" /> Genero il PDF…</> : <><FontAwesomeIcon icon={faFilePdf} /> Genera PDF ({riepilogo.daStampare})</>}
          </Button>
          {lotto.pdf_stato === 'ok' && lotto.pdf_path && (
            <Button variant="outline-success" onClick={() => apriPdf(lotto.pdf_path)}><FontAwesomeIcon icon={faDownload} /> Scarica l'ultimo PDF</Button>
          )}
        </div>
      </div>

      <Form.Check
        type="switch" id="dorso-colorato" className="mb-2"
        checked={lotto.dorso_colorato !== false}
        onChange={(e) => cambiaDorsoColorato(e.target.checked)}
        label="Dorso del colore del genere (spento: tutti i dorsi neri)"
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {lotto.pdf_stato === 'errore' && <Alert variant="danger">Generazione del PDF non riuscita: {lotto.pdf_errore}</Alert>}
      {lotto.stato_ricerca === 'in_corso' && (
        <div className="mb-3">
          <div className="small text-muted mb-1">Ricerca dei film in corso: {lotto.progresso} di {lotto.totale}</div>
          <ProgressBar animated now={lotto.totale ? (lotto.progresso / lotto.totale) * 100 : 0} />
        </div>
      )}
      {lotto.stato_ricerca === 'errore' && <Alert variant="warning">La ricerca si è interrotta. Usa "Cerca di nuovo i non trovati".</Alert>}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <ButtonGroup size="sm">
          {[['tutti', 'Tutti'], ['controllare', 'Da controllare'], ['avvisi', 'Con segnalazioni'], ['esclusi', 'Esclusi']].map(([k, et]) => (
            <Button key={k} variant={filtro === k ? 'primary' : 'outline-primary'} onClick={() => setFiltro(k)}>{et}</Button>
          ))}
        </ButtonGroup>
        <Form.Control style={{ maxWidth: '280px' }} size="sm" placeholder="Cerca per titolo o regista…" value={cerca} onChange={(e) => setCerca(e.target.value)} />
      </div>

      <Table hover responsive size="sm" className="align-middle">
        <thead>
          <tr><th style={{ width: '40px' }}>#</th><th style={{ width: '64px' }} /><th>Dalla mail</th><th>Film abbinato</th><th>Stato e segnalazioni</th><th style={{ width: '170px' }} /></tr>
        </thead>
        <tbody>
          {visibili.map(v => (
            <tr key={v.id} className={v.escluso ? 'text-muted' : (!v.confermato && v.stato === 'non_trovato' ? 'table-danger' : (!v.confermato && v.stato === 'dubbio' ? 'table-warning' : ''))}>
              <td>{v.ordine}</td>
              <td>
                {urlLocandina(v, 'w154')
                  ? <img src={urlLocandina(v, 'w154')} alt="" style={{ width: '48px', height: '72px', objectFit: 'cover', borderRadius: '3px' }} onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                  : <div style={{ width: '48px', height: '72px', background: '#e9ecef', borderRadius: '3px' }} />}
              </td>
              <td>
                <div>{v.riga_originale}</div>
              </td>
              <td>
                {v.titolo_originale ? (
                  <>
                    <strong>{v.titolo_originale}</strong>{v.anno ? ` (${v.anno})` : ''}
                    {v.titolo_italiano && <div className="small">Dorso: {v.titolo_italiano}</div>}
                    {v.generi && (
                      <div className="small">
                        {lotto.dorso_colorato !== false && (
                          <span title="Colore del dorso" style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '5px', border: '1px solid #999', background: (palette.find(p => p.chiave === (v.colore_dorso || v.colore_auto)) || {}).hex || '#222' }} />
                        )}
                        {v.generi}
                      </div>
                    )}
                    <div className="small text-muted">
                      {v.registi || '—'}{' '}
                      {v.tmdb_id && <a href={`https://www.themoviedb.org/${v.tipo === 'serie' ? 'tv' : 'movie'}/${v.tmdb_id}`} target="_blank" rel="noreferrer">TMDB</a>}
                      {v.imdb_id && <> · <a href={`https://www.imdb.com/title/${v.imdb_id}`} target="_blank" rel="noreferrer">IMDb</a></>}
                    </div>
                  </>
                ) : <span className="text-muted">—</span>}
              </td>
              <td>
                <div className="mb-1"><BadgeStato voce={v} />{v.stato === 'dubbio' && !v.confermato && v.confidenza != null && <span className="small text-muted ms-1">{Math.round(v.confidenza * 100)}%</span>}</div>
                {(v.avvisi || []).filter(a => !(a === 'non_trovato')).map(a => <BadgeAvviso key={a} codice={a} />)}
                {(v.avvisi_pdf || []).map(a => <BadgeAvviso key={`p-${a}`} codice={a} />)}
              </td>
              <td className="text-end text-nowrap">
                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => apriModifica(v)} title="Modifica dati, cambia film o locandina"><FontAwesomeIcon icon={faPen} /></Button>
                {!v.confermato && v.titolo_originale && (
                  <Button size="sm" variant="outline-success" className="me-1" onClick={() => alterna(v, 'confermato')} title="Conferma: è il film giusto"><FontAwesomeIcon icon={faCheck} /></Button>
                )}
                <Button size="sm" variant={v.escluso ? 'secondary' : 'outline-secondary'} className="me-1" onClick={() => alterna(v, 'escluso')} title={v.escluso ? 'Riammetti nel PDF' : 'Escludi dal PDF'}><FontAwesomeIcon icon={faBan} /></Button>
              </td>
            </tr>
          ))}
          {visibili.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">Nessun film in questo elenco.</td></tr>}
        </tbody>
      </Table>

      {/* Modale di modifica */}
      <Modal show={!!voceInModifica} onHide={() => setEditId(null)} size="xl" scrollable>
        {voceInModifica && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                {voceInModifica.riga_originale}
                <div className="small text-muted fw-normal"><BadgeStato voce={voceInModifica} /> {(voceInModifica.avvisi || []).filter(a => a !== 'non_trovato').map(a => <BadgeAvviso key={a} codice={a} />)}</div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={4}>
                  <div className="text-center mb-2">
                    {posterModulo
                      ? <img src={posterModulo} alt="Locandina" style={{ maxWidth: '100%', maxHeight: '340px', borderRadius: '4px', boxShadow: '0 1px 6px rgba(0,0,0,.3)' }} />
                      : <div className="text-muted py-5">Nessuna locandina</div>}
                    {voceInModifica.poster_locale && <div className="small text-muted mt-1">Locandina caricata a mano</div>}
                    {voceInModifica.poster_larghezza && !voceInModifica.poster_locale && <div className="small text-muted mt-1">{voceInModifica.poster_larghezza} px di larghezza</div>}
                  </div>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Carica una locandina tua</Form.Label>
                    <Form.Control type="file" size="sm" accept="image/*" onChange={caricaFile} disabled={occupato} />
                  </Form.Group>
                  {(voceInModifica.poster_alternativi || []).length > 0 && (
                    <>
                      <div className="small text-muted mb-1">Altre locandine su TMDB (clicca per sceglierla)</div>
                      <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {voceInModifica.poster_alternativi.map(p => (
                          <img
                            key={p.url} src={p.miniatura} alt="" title={`${p.lingua || 'senza testo'} · ${p.larghezza}×${p.altezza}`}
                            onClick={() => cambiaForm('poster_url', p.url)}
                            style={{ width: '58px', height: '87px', objectFit: 'cover', cursor: 'pointer', borderRadius: '3px', border: form.poster_url === p.url ? '3px solid #0d6efd' : '1px solid #ccc' }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </Col>
                <Col md={8}>
                  <Tabs defaultActiveKey="dati" className="mb-3">
                    <Tab eventKey="dati" title="Dati della copertina">
                      <Row>
                        <Col md={9}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Titolo originale</Form.Label>
                            <Form.Control value={form.titolo_originale || ''} onChange={(e) => cambiaForm('titolo_originale', e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Anno</Form.Label>
                            <Form.Control type="number" value={form.anno || ''} onChange={(e) => cambiaForm('anno', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">Titolo italiano (per il dorso)</Form.Label>
                        <Form.Control value={form.titolo_italiano || ''} onChange={(e) => cambiaForm('titolo_italiano', e.target.value)} placeholder="Se vuoto, sul dorso va il titolo originale" />
                      </Form.Group>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Etichetta</Form.Label>
                            <Form.Select value={form.etichetta_regia || 'REGISTA:'} onChange={(e) => cambiaForm('etichetta_regia', e.target.value)}>
                              <option value="REGISTA:">REGISTA:</option>
                              <option value="IDEATO DA:">IDEATO DA:</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={9}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Regista / registi (separati da " - ")</Form.Label>
                            <Form.Control value={form.registi || ''} onChange={(e) => cambiaForm('registi', e.target.value)} />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={7}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Genere (separati da " · ")</Form.Label>
                            <Form.Control value={form.generi || ''} onChange={(e) => cambiaForm('generi', e.target.value)} placeholder="es. Thriller · Dramma" />
                          </Form.Group>
                        </Col>
                        <Col md={5}>
                          <Form.Group className="mb-2">
                            <Form.Label className="mb-1">Colore del dorso</Form.Label>
                            <Form.Select value={form.colore_dorso || ''} onChange={(e) => cambiaForm('colore_dorso', e.target.value)}>
                              <option value="">Automatico (dal genere)</option>
                              {palette.map(p => <option key={p.chiave} value={p.chiave}>{p.nome}</option>)}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">Attori (separati da " - ")</Form.Label>
                        <Form.Control as="textarea" rows={2} value={form.attori || ''} onChange={(e) => cambiaForm('attori', e.target.value)} />
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label className="mb-1">Trama</Form.Label>
                        {voceInModifica.trama_lingua === 'en' && (form.trama || '') === (voceInModifica.trama || '') && (
                          <Alert variant="warning" className="py-1 small mb-1">Questa trama è in inglese: traducila qui sotto.</Alert>
                        )}
                        <Form.Control as="textarea" rows={9} value={form.trama || ''} onChange={(e) => cambiaForm('trama', e.target.value)} />
                        <Form.Text className={(form.trama || '').length > 1000 ? 'text-danger' : 'text-muted'}>
                          {(form.trama || '').length} caratteri. Nello spazio della copertina entrano circa 1000 caratteri a corpo normale; oltre si rimpicciolisce il testo.
                        </Form.Text>
                      </Form.Group>
                    </Tab>

                    <Tab eventKey="film" title={`Cambia film (${(voceInModifica.candidati || []).length} proposte)`}>
                      <Row className="g-2 mb-3">
                        <Col md={4}><Form.Control placeholder="Titolo" value={ricerca.titolo} onChange={(e) => setRicerca({ ...ricerca, titolo: e.target.value })} /></Col>
                        <Col md={3}><Form.Control placeholder="Regista" value={ricerca.regista} onChange={(e) => setRicerca({ ...ricerca, regista: e.target.value })} /></Col>
                        <Col md={2}><Form.Control placeholder="Anno" type="number" value={ricerca.anno} onChange={(e) => setRicerca({ ...ricerca, anno: e.target.value })} /></Col>
                        <Col md={2}>
                          <Form.Select value={ricerca.tipo} onChange={(e) => setRicerca({ ...ricerca, tipo: e.target.value })}>
                            <option value="film">Film</option>
                            <option value="serie">Serie</option>
                          </Form.Select>
                        </Col>
                        <Col md={1}><Button onClick={cercaFilm} disabled={occupato || !ricerca.titolo.trim()}>Cerca</Button></Col>
                      </Row>
                      <Table size="sm" className="align-middle">
                        <tbody>
                          {(voceInModifica.candidati || []).map(c => (
                            <tr key={`${c.tipo}-${c.id}`} className={voceInModifica.tmdb_id === c.id ? 'table-success' : ''}>
                              <td style={{ width: '50px' }}>
                                {c.poster && <img src={`https://image.tmdb.org/t/p/w92${c.poster}`} alt="" style={{ width: '40px', borderRadius: '3px' }} />}
                              </td>
                              <td>
                                <strong>{c.titolo}</strong>{c.originale && c.originale !== c.titolo ? ` (${c.originale})` : ''} {c.anno ? `· ${c.anno}` : ''}
                                <div className="small text-muted">
                                  {(c.registi || []).slice(0, 3).join(', ') || 'regista non indicato'}
                                  {c.da_regista && ' · dalla filmografia del regista'}
                                  {' · '}<a href={`https://www.themoviedb.org/${c.tipo === 'serie' ? 'tv' : 'movie'}/${c.id}`} target="_blank" rel="noreferrer">TMDB</a>
                                </div>
                              </td>
                              <td className="text-nowrap small text-muted">{Math.round((c.punteggio || 0) * 100)}%</td>
                              <td className="text-end">
                                <Button size="sm" variant={voceInModifica.tmdb_id === c.id ? 'success' : 'outline-primary'} disabled={occupato} onClick={() => scegli(c.id, c.tipo)}>
                                  {voceInModifica.tmdb_id === c.id ? 'Scelto' : 'Scegli'}
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {(voceInModifica.candidati || []).length === 0 && <tr><td className="text-muted">Nessuna proposta: prova con un altro titolo o con il solo regista.</td></tr>}
                        </tbody>
                      </Table>
                      <div className="d-flex gap-2 align-items-center mt-2">
                        <span className="small text-muted">Oppure l'id TMDB (dall'indirizzo della pagina del film):</span>
                        <Form.Control size="sm" style={{ maxWidth: '130px' }} value={tmdbManuale} onChange={(e) => setTmdbManuale(e.target.value)} placeholder="es. 436270" />
                        <Button size="sm" variant="outline-primary" disabled={occupato || !tmdbManuale.trim()} onClick={() => scegli(Number(tmdbManuale), ricerca.tipo)}>Usa questo</Button>
                      </div>
                      <Alert variant="light" className="small mt-3 mb-0">
                        Scegliendo un altro film i dati della copertina vengono sostituiti con quelli del nuovo film (le modifiche non salvate nella scheda "Dati" si perdono).
                      </Alert>
                    </Tab>
                  </Tabs>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
              <div>
                <Button variant="outline-danger" size="sm" onClick={() => { setEditId(null); elimina(voceInModifica); }}>Togli dal lotto</Button>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={anteprima} disabled={occupato || !form.titolo_originale}><FontAwesomeIcon icon={faEye} /> Anteprima PDF</Button>
                <Button variant="secondary" onClick={() => setEditId(null)}>Chiudi</Button>
                <Button variant="outline-primary" onClick={() => salvaEChiudi(false)} disabled={occupato}>Salva</Button>
                <Button variant="success" onClick={() => salvaEChiudi(true)} disabled={occupato}><FontAwesomeIcon icon={faCheck} /> Salva e conferma</Button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <Modal show={showAggiungi} onHide={() => setShowAggiungi(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Aggiungi altri film al lotto</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Control as="textarea" rows={10} value={testoAggiunta} onChange={(e) => setTestoAggiunta(e.target.value)} placeholder="Incolla qui altri film (un'altra mail dello stesso cliente)" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAggiungi(false)}>Annulla</Button>
          <Button onClick={aggiungi} disabled={occupato || !testoAggiunta.trim()}>Aggiungi e cerca</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CopertineDvdLotto;
